import React, { useRef, useState } from 'react'
import ImageUploader from '../image-uploader'
import { Promotion, PromotionItem } from '@ecountabl/lib'

type Props = {
  label?: string
  value?: Promotion | null
  onChange: (promotion: Promotion | null) => void
  isVisible?: boolean
}

const PromotionSection: React.FC<Props> = ({ label = 'Promotion', value, onChange, isVisible }: Props) => {
  const promotion: Promotion = value || {}
  const items: PromotionItem[] = promotion.items || []

  const setField = (field: keyof Promotion, val: string) => {
    onChange({ ...promotion, [field]: val })
  }

  const setItemField = (index: number, field: keyof PromotionItem, val: string) => {
    const nextItems = [...items]
    nextItems[index] = { ...nextItems[index], [field]: val }
    onChange({ ...promotion, items: nextItems })
  }

  const addItem = () => {
    onChange({ ...promotion, items: [...items, {}] })
  }

  const deleteItem = (index: number) => {
    const nextItems = [...items]
    nextItems.splice(index, 1)
    onChange({ ...promotion, items: nextItems })
  }

  const moveItem = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return
    const next = [...items]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    onChange({ ...promotion, items: next })
  }

  // Simple HTML5 drag-and-drop reorder
  const dragIndexRef = useRef<number | null>(null)
  const onDragStart = (index: number) => (ev: React.DragEvent<HTMLDivElement>) => {
    dragIndexRef.current = index
    ev.dataTransfer.effectAllowed = 'move'
  }
  const onDragOver = (ev: React.DragEvent<HTMLDivElement>) => {
    ev.preventDefault()
  }
  const onDrop = (index: number) => (ev: React.DragEvent<HTMLDivElement>) => {
    ev.preventDefault()
    const from = dragIndexRef.current
    dragIndexRef.current = null
    if (from === null || from === index) return
    moveItem(from, index)
  }

  const clearSection = () => {
    onChange(null)
  }

  return (
    <div className="form-group">
      <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
        {label}
        {typeof isVisible === 'boolean' && (
          <i
            className={`fa ${isVisible ? 'fa-eye' : 'fa-eye-slash'}`}
            title={isVisible ? 'Visible in app to users' : 'Hidden in app for users'}
            aria-label={isVisible ? 'Visible in app to users' : 'Hidden in app for users. Fill out required fields to make visible.'}
            style={{ cursor: 'help' }}
          />
        )}
      </h4>

      <label>Section Title</label>
      <input
        type="text"
        className="form-control"
        value={promotion.sectionTitle || ''}
        onChange={e => setField('sectionTitle', e.target.value)}
        placeholder="e.g., Special Offer"
      />

      <label style={{ marginTop: '1em' }}>Promotion Text</label>
      <textarea
        className="form-control"
        value={promotion.promotionText || ''}
        onChange={e => setField('promotionText', e.target.value)}
        placeholder="Description of the promotion"
      />

      <div style={{ marginTop: '1.5em' }}>
        <h5>Promotion Items</h5>
        {items.length === 0 ? (
          <div className="alert alert-secondary">No items added</div>
        ) : null}
        <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
          {items.map((it, i) => (
            <div
              key={i}
              draggable
              onDragStart={onDragStart(i)}
              onDragOver={onDragOver}
              onDrop={onDrop(i)}
              style={{
                border: '1px solid #ddd',
                borderRadius: 8,
                padding: '1em',
                background: '#fafafa',
                cursor: 'grab'
              }}
            >
              <div style={{ display: 'flex', gap: '1em', alignItems: 'flex-start' }}>
                <div style={{ width: 200 }}>
                  <label>Image</label>
                  <ImageUploader
                    imgSrc={it.image || ''}
                    onChange={(image) => setItemField(i, 'image', image)}
                    hidePrompt
                    targetSize={{ width: 200, height: 200 }}
                    forceTargetSize
                  />
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={it.name || ''}
                      onChange={e => setItemField(i, 'name', e.target.value)}
                      placeholder="e.g., Impact Checking"
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      className="form-control"
                      value={it.description || ''}
                      onChange={e => setItemField(i, 'description', e.target.value)}
                      placeholder="Short description"
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>URL</label>
                    <input
                      type="text"
                      className="form-control"
                      value={it.url || ''}
                      onChange={e => setItemField(i, 'url', e.target.value)}
                      placeholder="https://example.com/promo"
                    />
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5em', marginTop: '0.75em' }}>
                <button type="button" className="btn btn-normal" onClick={() => moveItem(i, i - 1)} disabled={i === 0}>Move Up</button>
                <button type="button" className="btn btn-normal" onClick={() => moveItem(i, i + 1)} disabled={i === items.length - 1}>Move Down</button>
                <button type="button" className="btn btn-danger" onClick={() => deleteItem(i)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
        <button type="button" className="btn btn-normal" style={{ marginTop: '1em' }} onClick={addItem}>Add Item</button>
      </div>

      <button type="button" className="btn btn-secondary" style={{ marginTop: '1em' }} onClick={clearSection}>
        Clear Section
      </button>
    </div>
  )
}

export default PromotionSection


